
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface Payout {
  id: string;
  provider_id: string;
  provider_name: string;
  provider_email: string;
  amount: number;
  status: 'pending' | 'approved' | 'processing' | 'completed' | 'rejected' | 'failed';
  payout_method: string;
  stripe_transfer_id?: string;
  stripe_account_id?: string;
  requested_at: string;
  processed_at?: string;
  completed_at?: string;
  rejection_reason?: string;
  notes?: string;
  auto_payout: boolean;
}

interface GlobalSettings {
  id: string;
  auto_payout_enabled: boolean;
  default_frequency: string;
  minimum_payout_threshold: number;
  payout_day_of_week: number;
  payout_day_of_month: number;
  processing_fee_percentage: number;
}

interface ProviderBalance {
  provider_id: string;
  provider_name: string;
  provider_email: string;
  available_balance: number;
  pending_balance: number;
  total_earned: number;
  last_payout_date?: string;
  stripe_account_id?: string;
  auto_payout_enabled: boolean;
}

export default function ProviderPayoutsManagement() {
  const [activeTab, setActiveTab] = useState<'requests' | 'history' | 'balances' | 'settings'>('requests');
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [providerBalances, setProviderBalances] = useState<ProviderBalance[]>([]);
  const [globalSettings, setGlobalSettings] = useState<GlobalSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState('all');
  const [selectedPayouts, setSelectedPayouts] = useState<string[]>([]);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<Payout | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSettingsConfirm, setShowSettingsConfirm] = useState(false);
  const [pendingSettings, setPendingSettings] = useState<Partial<GlobalSettings>>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([
      loadPayouts(),
      loadProviderBalances(),
      loadGlobalSettings()
    ]);
    setLoading(false);
  };

  const loadPayouts = async () => {
    const { data, error } = await supabase
      .from('provider_payouts')
      .select('*')
      .order('requested_at', { ascending: false });

    if (!error && data) {
      setPayouts(data);
    }
  };

  const loadProviderBalances = async () => {
    // Get all providers with their earnings
    const { data: providers } = await supabase
      .from('provider_profiles')
      .select('user_id, full_name, email, stripe_account_id');

    const { data: jobs } = await supabase
      .from('jobs')
      .select('provider_id, total_cost, status, completed_at')
      .eq('status', 'completed');

    const { data: completedPayouts } = await supabase
      .from('provider_payouts')
      .select('provider_id, amount, status, completed_at')
      .eq('status', 'completed');

    const { data: pendingPayouts } = await supabase
      .from('provider_payouts')
      .select('provider_id, amount, status')
      .in('status', ['pending', 'approved', 'processing']);

    const { data: payoutSettings } = await supabase
      .from('payout_settings')
      .select('*');

    const balances: ProviderBalance[] = (providers || []).map(provider => {
      const providerJobs = (jobs || []).filter(j => j.provider_id === provider.user_id);
      const totalEarned = providerJobs.reduce((sum, j) => sum + (parseFloat(j.total_cost) * 0.85), 0); // 85% to provider
      
      const paidOut = (completedPayouts || [])
        .filter(p => p.provider_id === provider.user_id)
        .reduce((sum, p) => sum + parseFloat(p.amount), 0);

      const pendingAmount = (pendingPayouts || [])
        .filter(p => p.provider_id === provider.user_id)
        .reduce((sum, p) => sum + parseFloat(p.amount), 0);

      const lastPayout = (completedPayouts || [])
        .filter(p => p.provider_id === provider.user_id)
        .sort((a, b) => new Date(b.completed_at!).getTime() - new Date(a.completed_at!).getTime())[0];

      const settings = (payoutSettings || []).find(s => s.provider_id === provider.user_id);

      return {
        provider_id: provider.user_id,
        provider_name: provider.full_name || 'Unknown Provider',
        provider_email: provider.email || '',
        available_balance: Math.max(0, totalEarned - paidOut - pendingAmount),
        pending_balance: pendingAmount,
        total_earned: totalEarned,
        last_payout_date: lastPayout?.completed_at,
        stripe_account_id: provider.stripe_account_id,
        auto_payout_enabled: settings?.auto_payout_enabled || false
      };
    }).filter(b => b.total_earned > 0);

    setProviderBalances(balances);
  };

  const loadGlobalSettings = async () => {
    const { data } = await supabase
      .from('global_payout_settings')
      .select('*')
      .single();

    if (data) {
      setGlobalSettings(data);
    }
  };

  const getFilteredPayouts = () => {
    let filtered = [...payouts];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.provider_name?.toLowerCase().includes(term) ||
        p.provider_email?.toLowerCase().includes(term) ||
        p.id.toLowerCase().includes(term)
      );
    }

    if (dateRange !== 'all') {
      const now = new Date();
      let startDate: Date;
      switch (dateRange) {
        case 'today':
          startDate = new Date(now.setHours(0, 0, 0, 0));
          break;
        case 'week':
          startDate = new Date(now.setDate(now.getDate() - 7));
          break;
        case 'month':
          startDate = new Date(now.setMonth(now.getMonth() - 1));
          break;
        default:
          startDate = new Date(0);
      }
      filtered = filtered.filter(p => new Date(p.requested_at) >= startDate);
    }

    return filtered;
  };

  const handleApprove = async (payout: Payout) => {
    setSelectedPayout(payout);
    setShowApproveModal(true);
  };

  const handleReject = (payout: Payout) => {
    setSelectedPayout(payout);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const processApproval = async () => {
    if (!selectedPayout) return;
    setProcessing(true);

    try {
      // Update status to approved
      const { error } = await supabase
        .from('provider_payouts')
        .update({
          status: 'approved',
          processed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedPayout.id);

      if (error) throw error;

      // In production, this would trigger Stripe transfer
      // For now, we'll simulate processing
      setTimeout(async () => {
        await supabase
          .from('provider_payouts')
          .update({
            status: 'completed',
            completed_at: new Date().toISOString(),
            stripe_transfer_id: `tr_${Date.now()}`
          })
          .eq('id', selectedPayout.id);

        loadPayouts();
      }, 2000);

      setShowApproveModal(false);
      loadPayouts();
    } catch (error) {
      console.error('Error approving payout:', error);
      alert('Failed to approve payout');
    } finally {
      setProcessing(false);
    }
  };

  const processRejection = async () => {
    if (!selectedPayout || !rejectionReason) return;
    setProcessing(true);

    try {
      const { error } = await supabase
        .from('provider_payouts')
        .update({
          status: 'rejected',
          rejection_reason: rejectionReason,
          processed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedPayout.id);

      if (error) throw error;

      setShowRejectModal(false);
      loadPayouts();
    } catch (error) {
      console.error('Error rejecting payout:', error);
      alert('Failed to reject payout');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedPayouts.length === 0) return;
    setProcessing(true);

    try {
      const { error } = await supabase
        .from('provider_payouts')
        .update({
          status: 'approved',
          processed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .in('id', selectedPayouts);

      if (error) throw error;

      setSelectedPayouts([]);
      loadPayouts();
    } catch (error) {
      console.error('Error bulk approving:', error);
      alert('Failed to approve payouts');
    } finally {
      setProcessing(false);
    }
  };

  const updateGlobalSettings = async (updates: Partial<GlobalSettings>) => {
    setPendingSettings(updates);
    setShowSettingsConfirm(true);
  };

  const confirmSettingsUpdate = async () => {
    if (!globalSettings) return;
    setProcessing(true);

    try {
      const { error } = await supabase
        .from('global_payout_settings')
        .update({
          ...pendingSettings,
          updated_at: new Date().toISOString()
        })
        .eq('id', globalSettings.id);

      if (error) throw error;

      setGlobalSettings({ ...globalSettings, ...pendingSettings });
      setShowSettingsConfirm(false);
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Failed to update settings');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-blue-100 text-blue-800';
      case 'processing': return 'bg-indigo-100 text-indigo-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'failed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDayName = (day: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[day] || 'Friday';
  };

  // Stats calculations
  const stats = {
    pendingRequests: payouts.filter(p => p.status === 'pending').length,
    pendingAmount: payouts.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0),
    processingAmount: payouts.filter(p => ['approved', 'processing'].includes(p.status)).reduce((sum, p) => sum + p.amount, 0),
    completedThisMonth: payouts.filter(p => {
      const date = new Date(p.completed_at || '');
      const now = new Date();
      return p.status === 'completed' && date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).reduce((sum, p) => sum + p.amount, 0),
    totalPaidOut: payouts.filter(p => p.status === 'completed').reduce((sum, p) => sum + p.amount, 0)
  };

  const filteredPayouts = getFilteredPayouts();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Provider Payouts</h2>
          <p className="text-gray-600 mt-1">Manage payout requests, view history, and configure automated transfers</p>
        </div>
        <button
          onClick={loadData}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i>
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/90">Pending Requests</p>
              <p className="text-3xl font-bold text-white mt-2">{stats.pendingRequests}</p>
              <p className="text-xs text-white/70 mt-1">£{stats.pendingAmount.toFixed(2)} total</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-time-line text-2xl text-white"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/90">Processing</p>
              <p className="text-3xl font-bold text-white mt-2">£{stats.processingAmount.toFixed(2)}</p>
              <p className="text-xs text-white/70 mt-1">Being transferred</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-loader-4-line text-2xl text-white"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/90">Paid This Month</p>
              <p className="text-3xl font-bold text-white mt-2">£{stats.completedThisMonth.toFixed(2)}</p>
              <p className="text-xs text-white/70 mt-1">Completed transfers</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-calendar-check-line text-2xl text-white"></i>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/90">Total Paid Out</p>
              <p className="text-3xl font-bold text-white mt-2">£{stats.totalPaidOut.toFixed(2)}</p>
              <p className="text-xs text-white/70 mt-1">All time</p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-money-pound-circle-line text-2xl text-white"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px overflow-x-auto">
            {[
              { id: 'requests', label: 'Payout Requests', icon: 'ri-file-list-3-line' },
              { id: 'history', label: 'Payout History', icon: 'ri-history-line' },
              { id: 'balances', label: 'Provider Balances', icon: 'ri-wallet-3-line' },
              { id: 'settings', label: 'Auto-Payout Settings', icon: 'ri-settings-3-line' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={`${tab.icon} w-5 h-5 flex items-center justify-center`}></i>
                {tab.label}
                {tab.id === 'requests' && stats.pendingRequests > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full">
                    {stats.pendingRequests}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Payout Requests Tab */}
          {activeTab === 'requests' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[200px]">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    placeholder="Search by provider name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="processing">Processing</option>
                  <option value="all">All Statuses</option>
                </select>
              </div>

              {/* Bulk Actions */}
              {selectedPayouts.length > 0 && (
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 flex items-center justify-between">
                  <span className="text-sm text-teal-800">
                    <strong>{selectedPayouts.length}</strong> payouts selected
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleBulkApprove}
                      disabled={processing}
                      className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      Approve All
                    </button>
                    <button
                      onClick={() => setSelectedPayouts([])}
                      className="px-3 py-1.5 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-colors text-sm cursor-pointer whitespace-nowrap"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              {/* Requests Table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedPayouts.length === filteredPayouts.filter(p => p.status === 'pending').length && filteredPayouts.filter(p => p.status === 'pending').length > 0}
                            onChange={() => {
                              const pendingIds = filteredPayouts.filter(p => p.status === 'pending').map(p => p.id);
                              setSelectedPayouts(selectedPayouts.length === pendingIds.length ? [] : pendingIds);
                            }}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredPayouts.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                            <i className="ri-inbox-line text-4xl text-gray-300 mb-2 block"></i>
                            No payout requests found
                          </td>
                        </tr>
                      ) : (
                        filteredPayouts.map((payout) => (
                          <tr key={payout.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-4">
                              {payout.status === 'pending' && (
                                <input
                                  type="checkbox"
                                  checked={selectedPayouts.includes(payout.id)}
                                  onChange={() => {
                                    setSelectedPayouts(prev =>
                                      prev.includes(payout.id)
                                        ? prev.filter(id => id !== payout.id)
                                        : [...prev, payout.id]
                                    );
                                  }}
                                  className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                                />
                              )}
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                                  <i className="ri-user-line text-teal-600"></i>
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">{payout.provider_name || 'Unknown'}</p>
                                  <p className="text-xs text-gray-500">{payout.provider_email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-lg font-bold text-gray-900">£{payout.amount.toFixed(2)}</p>
                            </td>
                            <td className="px-4 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(payout.status)}`}>
                                {payout.status}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${payout.auto_payout ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                                {payout.auto_payout ? 'Auto' : 'Manual'}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-500">
                              {new Date(payout.requested_at).toLocaleDateString()}
                              <br />
                              <span className="text-xs">{new Date(payout.requested_at).toLocaleTimeString()}</span>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => {
                                    setSelectedPayout(payout);
                                    setShowDetailsModal(true);
                                  }}
                                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer"
                                  title="View Details"
                                >
                                  <i className="ri-eye-line"></i>
                                </button>
                                {payout.status === 'pending' && (
                                  <>
                                    <button
                                      onClick={() => handleApprove(payout)}
                                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg cursor-pointer"
                                      title="Approve"
                                    >
                                      <i className="ri-check-line"></i>
                                    </button>
                                    <button
                                      onClick={() => handleReject(payout)}
                                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg cursor-pointer"
                                      title="Reject"
                                    >
                                      <i className="ri-close-line"></i>
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Payout History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[200px]">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    placeholder="Search completed payouts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                </select>
              </div>

              {/* History Table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transfer ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {payouts.filter(p => ['completed', 'rejected', 'failed'].includes(p.status)).length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                            <i className="ri-history-line text-4xl text-gray-300 mb-2 block"></i>
                            No payout history yet
                          </td>
                        </tr>
                      ) : (
                        payouts
                          .filter(p => ['completed', 'rejected', 'failed'].includes(p.status))
                          .map((payout) => (
                            <tr key={payout.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                    <i className="ri-user-line text-gray-600"></i>
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">{payout.provider_name || 'Unknown'}</p>
                                    <p className="text-xs text-gray-500">{payout.provider_email}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-4">
                                <p className="text-lg font-bold text-gray-900">£{payout.amount.toFixed(2)}</p>
                              </td>
                              <td className="px-4 py-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(payout.status)}`}>
                                  {payout.status}
                                </span>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-500 font-mono">
                                {payout.stripe_transfer_id || '-'}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-500">
                                {payout.completed_at ? new Date(payout.completed_at).toLocaleDateString() : '-'}
                              </td>
                              <td className="px-4 py-4 text-right">
                                <button
                                  onClick={() => {
                                    setSelectedPayout(payout);
                                    setShowDetailsModal(true);
                                  }}
                                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer"
                                >
                                  <i className="ri-eye-line"></i>
                                </button>
                              </td>
                            </tr>
                          ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Provider Balances Tab */}
          {activeTab === 'balances' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Provider Earnings & Balances</h3>
                <span className="text-sm text-gray-500">{providerBalances.length} providers with earnings</span>
              </div>

              <div className="grid gap-4">
                {providerBalances.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <i className="ri-wallet-3-line text-4xl text-gray-300 mb-2 block"></i>
                    No provider balances to display
                  </div>
                ) : (
                  providerBalances.map((balance) => (
                    <div key={balance.provider_id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                            <i className="ri-user-star-line text-xl text-teal-600"></i>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{balance.provider_name}</h4>
                            <p className="text-sm text-gray-500">{balance.provider_email}</p>
                            {balance.auto_payout_enabled && (
                              <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                <i className="ri-repeat-line"></i>
                                Auto-payout enabled
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <p className="text-xs text-gray-500 uppercase">Available</p>
                            <p className="text-xl font-bold text-green-600">£{balance.available_balance.toFixed(2)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500 uppercase">Pending</p>
                            <p className="text-xl font-bold text-amber-600">£{balance.pending_balance.toFixed(2)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500 uppercase">Total Earned</p>
                            <p className="text-xl font-bold text-gray-900">£{balance.total_earned.toFixed(2)}</p>
                          </div>
                          <div className="text-center min-w-[100px]">
                            <p className="text-xs text-gray-500 uppercase">Last Payout</p>
                            <p className="text-sm font-medium text-gray-700">
                              {balance.last_payout_date 
                                ? new Date(balance.last_payout_date).toLocaleDateString()
                                : 'Never'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Auto-Payout Settings Tab */}
          {activeTab === 'settings' && globalSettings && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-settings-3-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Automated Payout System</h3>
                    <p className="text-gray-600 mt-1">
                      Configure automatic payouts to providers based on schedule and thresholds. 
                      Providers can also opt-in to automatic payouts from their dashboard.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Master Toggle */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-semibold text-gray-900">Auto-Payout System</h4>
                      <p className="text-sm text-gray-500">Enable or disable automatic payouts globally</p>
                    </div>
                    <button
                      onClick={() => updateGlobalSettings({ auto_payout_enabled: !globalSettings.auto_payout_enabled })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                        globalSettings.auto_payout_enabled ? 'bg-teal-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          globalSettings.auto_payout_enabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <div className={`p-3 rounded-lg ${globalSettings.auto_payout_enabled ? 'bg-green-50 text-green-800' : 'bg-gray-50 text-gray-600'}`}>
                    <div className="flex items-center gap-2">
                      <i className={`${globalSettings.auto_payout_enabled ? 'ri-check-line' : 'ri-close-line'}`}></i>
                      <span className="text-sm font-medium">
                        {globalSettings.auto_payout_enabled ? 'Auto-payouts are active' : 'Auto-payouts are disabled'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Payout Frequency */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Default Payout Frequency</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {['daily', 'weekly', 'biweekly', 'monthly'].map((freq) => (
                      <button
                        key={freq}
                        onClick={() => updateGlobalSettings({ default_frequency: freq })}
                        className={`p-3 rounded-lg border-2 transition-all cursor-pointer capitalize ${
                          globalSettings.default_frequency === freq
                            ? 'border-teal-500 bg-teal-50 text-teal-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        {freq}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Minimum Threshold */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Minimum Payout Threshold</h4>
                  <p className="text-sm text-gray-500 mb-4">
                    Providers must have at least this amount to receive automatic payouts
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">£</span>
                      <input
                        type="number"
                        value={globalSettings.minimum_payout_threshold}
                        onChange={(e) => updateGlobalSettings({ minimum_payout_threshold: parseFloat(e.target.value) || 0 })}
                        min="0"
                        step="10"
                        className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    {[25, 50, 100, 200].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => updateGlobalSettings({ minimum_payout_threshold: amount })}
                        className={`px-3 py-1 text-sm rounded-lg transition-colors cursor-pointer ${
                          globalSettings.minimum_payout_threshold === amount
                            ? 'bg-teal-100 text-teal-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        £{amount}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Payout Day */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Payout Schedule Day</h4>
                  {globalSettings.default_frequency === 'weekly' || globalSettings.default_frequency === 'biweekly' ? (
                    <>
                      <p className="text-sm text-gray-500 mb-4">Select the day of the week for automatic payouts</p>
                      <select
                        value={globalSettings.payout_day_of_week}
                        onChange={(e) => updateGlobalSettings({ payout_day_of_week: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
                      >
                        {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                          <option key={day} value={day}>{getDayName(day)}</option>
                        ))}
                      </select>
                    </>
                  ) : globalSettings.default_frequency === 'monthly' ? (
                    <>
                      <p className="text-sm text-gray-500 mb-4">Select the day of the month for automatic payouts</p>
                      <select
                        value={globalSettings.payout_day_of_month}
                        onChange={(e) => updateGlobalSettings({ payout_day_of_month: parseInt(e.target.value) })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
                      >
                        {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                          <option key={day} value={day}>{day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'}</option>
                        ))}
                      </select>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">Daily payouts are processed every day at midnight</p>
                  )}
                </div>
              </div>

              {/* Summary Card */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h4 className="font-semibold text-gray-900 mb-4">Current Configuration Summary</h4>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-white rounded-lg">
                    <i className={`text-2xl mb-2 block ${globalSettings.auto_payout_enabled ? 'ri-check-double-line text-green-600' : 'ri-close-circle-line text-red-600'}`}></i>
                    <p className="text-sm text-gray-500">Status</p>
                    <p className="font-semibold text-gray-900">{globalSettings.auto_payout_enabled ? 'Active' : 'Disabled'}</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <i className="ri-calendar-line text-2xl text-teal-600 mb-2 block"></i>
                    <p className="text-sm text-gray-500">Frequency</p>
                    <p className="font-semibold text-gray-900 capitalize">{globalSettings.default_frequency}</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <i className="ri-money-pound-box-line text-2xl text-amber-600 mb-2 block"></i>
                    <p className="text-sm text-gray-500">Min. Threshold</p>
                    <p className="font-semibold text-gray-900">£{globalSettings.minimum_payout_threshold}</p>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg">
                    <i className="ri-time-line text-2xl text-indigo-600 mb-2 block"></i>
                    <p className="text-sm text-gray-500">Payout Day</p>
                    <p className="font-semibold text-gray-900">
                      {globalSettings.default_frequency === 'daily' 
                        ? 'Daily' 
                        : globalSettings.default_frequency === 'monthly'
                          ? `${globalSettings.payout_day_of_month}${globalSettings.payout_day_of_month === 1 ? 'st' : globalSettings.payout_day_of_month === 2 ? 'nd' : globalSettings.payout_day_of_month === 3 ? 'rd' : 'th'}`
                          : getDayName(globalSettings.payout_day_of_week)
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && selectedPayout && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Approve Payout</h3>
              <p className="text-gray-600 mt-2">
                Confirm payout of <strong>£{selectedPayout.amount.toFixed(2)}</strong> to {selectedPayout.provider_name}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Provider:</span>
                <span className="font-medium text-gray-900">{selectedPayout.provider_name}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Amount:</span>
                <span className="font-medium text-gray-900">£{selectedPayout.amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Method:</span>
                <span className="font-medium text-gray-900">Stripe Connect</span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowApproveModal(false)}
                disabled={processing}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={processApproval}
                disabled={processing}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="ri-check-line"></i>
                    Approve & Transfer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedPayout && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-close-line text-3xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Reject Payout</h3>
              <p className="text-gray-600 mt-2">
                Reject payout request of <strong>£{selectedPayout.amount.toFixed(2)}</strong> from {selectedPayout.provider_name}
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Rejection Reason *</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                rows={3}
                maxLength={500}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">{rejectionReason.length}/500 characters</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                disabled={processing}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={processRejection}
                disabled={processing || !rejectionReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="ri-close-line"></i>
                    Reject Payout
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedPayout && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Payout Details</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Status Banner */}
              <div className={`p-4 rounded-lg ${
                selectedPayout.status === 'completed' ? 'bg-green-50 border border-green-200' :
                selectedPayout.status === 'pending' ? 'bg-yellow-50 border border-yellow-200' :
                selectedPayout.status === 'rejected' ? 'bg-red-50 border border-red-200' :
                'bg-blue-50 border border-blue-200'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    selectedPayout.status === 'completed' ? 'bg-green-100' :
                    selectedPayout.status === 'pending' ? 'bg-yellow-100' :
                    selectedPayout.status === 'rejected' ? 'bg-red-100' : 'bg-blue-100'
                  }`}>
                    <i className={`text-xl ${
                      selectedPayout.status === 'completed' ? 'ri-check-line text-green-600' :
                      selectedPayout.status === 'pending' ? 'ri-time-line text-yellow-600' :
                      selectedPayout.status === 'rejected' ? 'ri-close-line text-red-600' :
                      'ri-loader-4-line text-blue-600'
                    }`}></i>
                  </div>
                  <div>
                    <p className={`font-semibold capitalize ${
                      selectedPayout.status === 'completed' ? 'text-green-800' :
                      selectedPayout.status === 'pending' ? 'text-yellow-800' :
                      selectedPayout.status === 'rejected' ? 'text-red-800' : 'text-blue-800'
                    }`}>
                      Payout {selectedPayout.status}
                    </p>
                    <p className="text-sm text-gray-600">
                      Requested {new Date(selectedPayout.requested_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Amount */}
              <div className="text-center py-4">
                <p className="text-sm text-gray-600 mb-1">Payout Amount</p>
                <p className="text-4xl font-bold text-gray-900">£{selectedPayout.amount.toFixed(2)}</p>
              </div>

              {/* Details Grid */}
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Provider</span>
                  <span className="font-medium text-gray-900">{selectedPayout.provider_name}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Email</span>
                  <span className="font-medium text-gray-900">{selectedPayout.provider_email}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Payout Type</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedPayout.auto_payout ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                    {selectedPayout.auto_payout ? 'Automatic' : 'Manual Request'}
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Method</span>
                  <span className="font-medium text-gray-900">Stripe Connect</span>
                </div>
                {selectedPayout.stripe_transfer_id && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Transfer ID</span>
                    <span className="font-mono text-sm text-gray-900">{selectedPayout.stripe_transfer_id}</span>
                  </div>
                )}
                {selectedPayout.completed_at && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Completed</span>
                    <span className="font-medium text-gray-900">{new Date(selectedPayout.completed_at).toLocaleString()}</span>
                  </div>
                )}
                {selectedPayout.rejection_reason && (
                  <div className="py-2">
                    <span className="text-gray-600 block mb-2">Rejection Reason</span>
                    <p className="bg-red-50 text-red-800 p-3 rounded-lg text-sm">{selectedPayout.rejection_reason}</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              {selectedPayout.status === 'pending' && (
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      handleReject(selectedPayout);
                    }}
                    className="flex-1 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium cursor-pointer whitespace-nowrap"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      handleApprove(selectedPayout);
                    }}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium cursor-pointer whitespace-nowrap"
                  >
                    Approve
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Settings Confirmation Modal */}
      {showSettingsConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-settings-3-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Update Settings</h3>
              <p className="text-gray-600 mt-2">
                Are you sure you want to update the payout settings? This will affect all future automatic payouts.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowSettingsConfirm(false)}
                disabled={processing}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmSettingsUpdate}
                disabled={processing}
                className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {processing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-check-line"></i>
                    Confirm
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
